import type { DevicePrinter } from "@/stores/device-printer-store";
import type { PrinterConfig } from "@/utils/pos-printer/types";

/**
 * The printer built into a Sunmi POS device (V2 / V2 Pro) is 58 mm wide at
 * 384 dots, which is 32 columns in ESC/POS font A — one narrower than the
 * generic 58 mm Bluetooth printers, whose 33rd column would wrap onto a line
 * of its own on this hardware.
 */
export const SUNMI_58MM_LINE_WIDTH = 32;

const BLUETOOTH_58MM_LINE_WIDTH = 33;
const LINE_WIDTH_80MM = 48;

/** Sunmi's printer stops with the last line still inside the mechanism. */
const SUNMI_TEAR_OFF_FEED_LINES = 5;
const BLUETOOTH_FEED_LINES = 2;

/**
 * Render config for the device-local printer. Receipts are always rendered as
 * generic ESC/POS; a Bluetooth printer's line width follows the paper width
 * chosen on the printer screen (80 mm → 48 chars, 58 mm → 33 — the same two
 * column counts the QZ Tray printer setup offers), while Sunmi's built-in
 * printer is fixed by its hardware.
 */
export function buildDevicePrinterRenderConfig(
  printer: DevicePrinter,
): NonNullable<PrinterConfig> {
  const shared = {
    id: 0,
    locationId: 0,
    printerName: printer.name,
    printerType: "escpos_generic",
    qzTrayHostIp: null,
    htmlRootFontSize: 12,
    paddingTop: 0,
  } satisfies Partial<NonNullable<PrinterConfig>>;

  if (printer.kind === "sunmi-internal") {
    return {
      ...shared,
      lineWidth: SUNMI_58MM_LINE_WIDTH,
      paddingBottom: SUNMI_TEAR_OFF_FEED_LINES,
    };
  }

  return {
    ...shared,
    lineWidth:
      printer.paperWidth === 80 ? LINE_WIDTH_80MM : BLUETOOTH_58MM_LINE_WIDTH,
    paddingBottom: BLUETOOTH_FEED_LINES,
  };
}
